import { checkIfCurrentStepIsValid, setCurrentStep } from "./setupWizardSlice";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApplicationState } from "..";
import {
  fetchStandard,
  fetchTestSession,
  getRequiredTestUseCases,
  fetchTestCasesForSession,
  fetchSessionStep2Selections,
  fetchConfigurations,
  uploadTestFile,
  deleteTestFile,
  requireAssist,
  deleteTestFiles,
  DowloadTestMaterials,
} from "../api";
import {
  TestSessionTypeDto,
  TestSessionVM,
  StandardVM,
  FunctionOptionSelection,
  ImportExportOption,
  ConformityLevel,
  TestCaseDto,
  TestCaseProfileDto,
  TestCaseFunctionDto,
  Step2VM,
  FunctionProfileDependencyDto,
  TestConfigurationDto,
  TestFileDto,
  TestSessionStatusDto,
  FunctionFunctionDependencyDto,
} from "../../autogenerated/models";
import {
  getProfileConformityLevel,
  getProfileFieldValue,
  getUnmetProfileDependencies,
  minExportLevelForProfiles,
  minImportLevelForProfiles,
  ProfileField,
} from "../../helpers/testSelectionHelpers";
import { TC50 } from "../../store/constants"

interface IdValue<T> {
  id: string;
  value: T;
}

interface UpdateImportExportAction {
  index: number;
  value: ImportExportOption;
}

interface SetProfileUnmetDependenciesAction {
  index: number;
  value: Array<string>;
}

interface SetFunctionProfileUnmetDependenciesAction {
  index: number;
  import: Array<FunctionProfileDependencyDto>;
  export: Array<FunctionProfileDependencyDto>;
}

interface UpdateConformityLevelAction {
  index: number;
  value: ConformityLevel;
}
export interface IValueText {
  value: string;
  text: string;
}

interface IStep2 {
  isLoading: boolean;
  isRefreshing: boolean;
  isGeneratingMaterials: boolean;
  standard: StandardVM["standard"];
  testCases: StandardVM["testCases"];
  functions: StandardVM["functions"];
  profiles: StandardVM["profiles"];
}

interface IStep3 {
  isLoading: boolean;
  testCases: TestCaseDto[];
  configurations: TestConfigurationDto[];
  modalTestCase: TestCaseDto;
  modalConfiguration: TestConfigurationDto;
  isModalLoading: boolean;
}

export interface SessionSetupState {
  isLoading: boolean;
  session: TestSessionVM["testSession"];
  testSessionTypes: TestSessionVM["testSessionTypes"];
  standards: TestSessionVM["testStandards"];
  step2: IStep2;
  step3: IStep3;
}

const initialState: SessionSetupState = {
  isLoading: false,
  session: {},
  testSessionTypes: [],
  standards: [],

  step2: {
    isLoading: false,
    isRefreshing: false,
    standard: undefined,
    isGeneratingMaterials: false,
    testCases: [],
    functions: [],
    profiles: [],
  },

  step3: {
    isLoading: false,
    testCases: [],
    configurations: [],
    modalTestCase: {},
    modalConfiguration: {},
    isModalLoading: false,
  },
};

let isTestCaseTuc50Active: boolean = false;

export const sessionSetupSlice = createSlice({
  name: "setupSession",
  initialState,
  reducers: {
    reset: (state) => initialState,

    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setSession: (state, action: PayloadAction<TestSessionVM>) => {
      state.session = action.payload.testSession;
      state.testSessionTypes = action.payload.testSessionTypes;
      state.standards = action.payload.testStandards;
    },

    setSessionName: (state, action: PayloadAction<string>) => {
      state.session!.name = action.payload;
    },

    setSessionStatus: (state, action: PayloadAction<TestSessionStatusDto>) => {
      state.session!.status = action.payload;
    },

    changeSessionType: (state, action: PayloadAction<TestSessionTypeDto>) => {
      state.session!.testSessionTypeId = action.payload.id;
      state.session!.testSessionType = action.payload;
    },

    setDeclarationFormValue: (
      state,
      action: PayloadAction<IdValue<string>>
    ) => {
      const { id, value } = action.payload;
      (state.session as any).declarationOfConformity[id] = value;
    },

    setAgree: (state, action: PayloadAction<boolean>) => {
      state.session!.declarationOfConformity!.agree = action.payload;
    },

    setIsLoadingStep2: (state, action: PayloadAction<boolean>) => {
      state.step2.isLoading = action.payload;
    },

    setisRefreshingStep2: (state, action: PayloadAction<boolean>) => {
      state.step2.isRefreshing = action.payload;
    },

    setIsGeneratingMaterialsStep2: (state, action: PayloadAction<boolean>) => {
      state.step2.isGeneratingMaterials = action.payload;
    },

    setIsLoadingStep3: (state, action: PayloadAction<boolean>) => {
      state.step3.isLoading = action.payload;
    },

    setIsLoadingStep3Modal: (state, action: PayloadAction<boolean>) => {
      state.step3.isModalLoading = action.payload;
    },

    setStep3TestCases: (state, action: PayloadAction<TestCaseDto[]>) => {
      state.step3.testCases = action.payload;
    },

    setStep3Configurations: (
      state,
      action: PayloadAction<TestConfigurationDto[]>
    ) => {
      state.step3.configurations = action.payload;
    },

    updateProfile: (state, action: PayloadAction<TestCaseProfileDto>) => {
      let index = (state.step2.profiles ? state.step2.profiles : []).findIndex(
        (p) => p.id === action.payload.id
      );
      (state.step2.profiles ? state.step2.profiles : [])[index] = {
        ...action.payload,
      };
    },

    updateSelections: (state, action: PayloadAction<any>) => {
      state.step2.profiles = [...action.payload.profiles];
      let deepCopy = JSON.parse(JSON.stringify(action.payload.functions));
      state.step2.functions = [...deepCopy];
    },

    updateProfiles: (state, action: PayloadAction<TestCaseProfileDto[]>) => {
      state.step2.profiles = [...action.payload];
    },

    updateFunctions: (state, action: PayloadAction<TestCaseFunctionDto[]>) => {
      let deepCopy = JSON.parse(JSON.stringify(action.payload));
      state.step2.functions = [...deepCopy];
    },

    changeProfileIndividualImportSelection: (
      state,
      action: PayloadAction<UpdateImportExportAction>
    ) => {
      (state.step2.profiles &&
      state.step2.profiles[action.payload.index] &&
      state.step2.profiles[action.payload.index].importSelections
        ? state.step2.profiles[action.payload.index].importSelections
        : {})!.individualOption!.selection = action.payload.value;
    },

    changeProfileIndividualExportSelection: (
      state,
      action: PayloadAction<UpdateImportExportAction>
    ) => {
      (state.step2.profiles &&
      state.step2.profiles[action.payload.index] &&
      state.step2.profiles[action.payload.index].exportSelections
        ? state.step2.profiles[action.payload.index].exportSelections
        : {})!.individualOption!.selection = action.payload.value;
    },

    changeProfileInclusiveImportSelection: (
      state,
      action: PayloadAction<UpdateImportExportAction>
    ) => {
      (state.step2.profiles &&
      state.step2.profiles[action.payload.index] &&
      state.step2.profiles[action.payload.index].importSelections
        ? state.step2.profiles[action.payload.index].importSelections
        : {})!.inclusiveOption!.selection = action.payload.value;
    },

    changeProfileInclusiveExportSelection: (
      state,
      action: PayloadAction<UpdateImportExportAction>
    ) => {
      (state.step2.profiles &&
      state.step2.profiles[action.payload.index] &&
      state.step2.profiles[action.payload.index].exportSelections
        ? state.step2.profiles[action.payload.index].exportSelections
        : {})!.inclusiveOption!.selection = action.payload.value;
    },

    changeProfileDifferenceImportSelection: (
      state,
      action: PayloadAction<UpdateImportExportAction>
    ) => {
      (state.step2.profiles &&
      state.step2.profiles[action.payload.index] &&
      state.step2.profiles[action.payload.index].importSelections
        ? state.step2.profiles[action.payload.index].importSelections
        : {})!.differenceOption!.selection = action.payload.value;
    },

    changeProfileDifferenceExportSelection: (
      state,
      action: PayloadAction<UpdateImportExportAction>
    ) => {
      (state.step2.profiles &&
      state.step2.profiles[action.payload.index] &&
      state.step2.profiles[action.payload.index].exportSelections
        ? state.step2.profiles[action.payload.index].exportSelections
        : {})!.differenceOption!.selection = action.payload.value;
    },

    setProfileUmnetDependencies: (
      state,
      action: PayloadAction<SetProfileUnmetDependenciesAction>
    ) => {
      (state.step2.profiles
        ? state.step2.profiles[action.payload.index]
        : {}
      ).unmetProfileDependencies = [...action.payload.value];
    },

    setFunctionImportExportProfileUmnetDependencies: (
      state,
      action: PayloadAction<SetFunctionProfileUnmetDependenciesAction>
    ) => {
      (state.step2.functions
        ? state.step2.functions[action.payload.index]
        : ({} as TestCaseFunctionDto)
      ).unmetImportProfileDependencies = [...action.payload.import];

      (state.step2.functions
        ? state.step2.functions[action.payload.index]
        : ({} as TestCaseFunctionDto)
      ).unmetExportProfileDependencies = [...action.payload.export];
    },

    setFunctionConformityLevel: (
      state,
      action: PayloadAction<UpdateConformityLevelAction>
    ) => {
      (state.step2.functions
        ? state.step2.functions[action.payload.index]
        : ({} as TestCaseFunctionDto)
      ).conformityOption = action.payload.value;
    },

    changeFunctionSelection: (
      state,
      action: PayloadAction<FunctionOptionSelection>
    ) => {
      var index = state.step2.functions?.findIndex(
        (p) => p.id === action.payload.functionId
      );
      if (
        index !== undefined &&
        index !== -1 &&
        state.step2.functions &&
        state.step2.functions[index]
      ) {
        state.step2.functions[index].conformityOption = action.payload.option;
      }
    },

    setTestCases: (state, action: PayloadAction<TestCaseDto[]>) => {
      state.step2.testCases = action.payload;
    },

    setProfiles: (state, action: PayloadAction<TestCaseProfileDto[]>) => {
      state.step2.profiles = action.payload;
    },

    setFunctions: (state, action: PayloadAction<TestCaseFunctionDto[]>) => {
      state.step2.functions = action.payload;
    },

    // changeFunctionSelection: (state, action: PayloadAction<IStep2['selectedFunctions']>) => {
    //   //state.data.step2.selectedFunctions = action.payload
    // },

    setStandard: (state, action: PayloadAction<StandardVM>) => {
      state.step2.standard = action.payload.standard;
      state.step2.functions = action.payload.functions;
      state.step2.profiles = action.payload.profiles;
      state.step2.testCases = action.payload.testCases;
    },

    setModalTestCase: (state, action: PayloadAction<TestCaseDto>) => {
      state.step3.modalTestCase = action.payload;
    },

    setModalConfiguration: (
      state,
      action: PayloadAction<TestConfigurationDto>
    ) => {
      state.step3.modalConfiguration = action.payload;
    },

    setTestCasernalEvaluationAnswer: (
      state,
      action: PayloadAction<boolean>
    ) => {
      let testCaseId = state.step3.modalTestCase.id;
      let index = state.step3.testCases.findIndex((tc) => tc.id === testCaseId);
      state.step3.testCases[index].askForInternalEvaluation = false;
      state.step3.testCases[index].internalEvaluation = action.payload;
    },

    setModalTestCasernalEvaluationAnswer: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.step3.modalTestCase.askForInternalEvaluation = false;
      state.step3.modalTestCase.internalEvaluation = action.payload;
    },

    addConfigurationToTestCase: (
      state,
      action: PayloadAction<TestConfigurationDto>
    ) => {
      let testCaseId = state.step3.modalTestCase.id;
      let index = state.step3.testCases.findIndex((tc) => tc.id === testCaseId);
      state.step3.testCases[index].testConfigurations = [
        ...(state.step3.testCases[index].testConfigurations ?? []),
        action.payload,
      ];
      state.step3.modalTestCase.testConfigurations = [
        ...(state.step3.modalTestCase.testConfigurations ?? []),
        action.payload,
      ];
    },

    removeConfigurationFromTestCase: (state, action: PayloadAction<string>) => {
      let testCaseId = state.step3.modalTestCase.id;
      let index = state.step3.testCases.findIndex((tc) => tc.id === testCaseId);
      let confIndex =
        state.step3.testCases[index].testConfigurations?.findIndex(
          (conf) => conf.id === action.payload
        ) ?? 0;
      state.step3.testCases[index].testConfigurations?.splice(confIndex, 1);
      state.step3.modalTestCase.testConfigurations?.splice(confIndex, 1);
    },

    setTestFile: (state, action: PayloadAction<TestFileDto>) => {
      let testCasesIndex = state.step3.testCases.findIndex(
        (tc) => tc.id === state.step3.modalTestCase.id
      );
      let testStepIndex =
        state.step3.testCases[testCasesIndex].testSteps?.findIndex(
          (ts) => ts.id === action.payload.testStepId
        ) ?? -1;

      (state.step3.modalTestCase.testSteps &&
      state.step3.modalTestCase.testSteps[testStepIndex]
        ? state.step3.modalTestCase.testSteps[testStepIndex]
        : {}
      ).testFiles?.push(action.payload);

      (state.step3.testCases[testCasesIndex].testSteps
        ? state.step3.testCases[testCasesIndex].testSteps
        : [])![testStepIndex].testFiles?.push(action.payload);
    },

    deleteTestFile: (state, action: PayloadAction<TestFileDto>) => {
      let testCasesIndex = state.step3.testCases.findIndex(
        (tc) => tc.id === state.step3.modalTestCase.id
      );
      let testStepIndex =
        state.step3.testCases[testCasesIndex].testSteps?.findIndex(
          (ts) => ts.id === action.payload.testStepId
        ) ?? -1;

      let modalFileIndex =
        (state.step3.modalTestCase.testSteps &&
        state.step3.modalTestCase.testSteps[testStepIndex]
          ? state.step3.modalTestCase.testSteps[testStepIndex]
          : {}
        ).testFiles?.findIndex((tf) => tf.id === action.payload.id) ?? 0;

      let stepFileIndex =
        (state.step3.testCases[testCasesIndex].testSteps
          ? state.step3.testCases[testCasesIndex].testSteps
          : [])![testStepIndex].testFiles?.findIndex(
          (tf) => tf.id === action.payload.id
        ) ?? 0;

      (state.step3.modalTestCase.testSteps &&
      state.step3.modalTestCase.testSteps[testStepIndex]
        ? state.step3.modalTestCase.testSteps[testStepIndex]
        : {}
      ).testFiles?.splice(modalFileIndex, 1);

      (state.step3.testCases[testCasesIndex].testSteps
        ? state.step3.testCases[testCasesIndex].testSteps
        : [])![testStepIndex].testFiles?.splice(stepFileIndex, 1);
    },
  },
});

export const { actions } = sessionSetupSlice;

export const resetSessionSetup = () => (dispatch: any) => {
  dispatch(actions.reset());
  dispatch(checkIfCurrentStepIsValid());
};

/*-------------- STEP 1 -----------*/
export const changeSessionName = (sessionName: string) => (dispatch: any) => {
  dispatch(actions.setSessionName(sessionName));
  dispatch(checkIfCurrentStepIsValid());
};

export const changeSessionType =
  (newSessionTypeId: string) =>
  (dispatch: any, getState: () => ApplicationState) => {
    var state = getState();
    var newSession = state.sessionSetup.testSessionTypes?.find(
      (f) => f.id === newSessionTypeId
    );
    if (newSession) {
      dispatch(actions.changeSessionType(newSession));
    }
    dispatch(checkIfCurrentStepIsValid());
  };

export const changeDeclarationFormValue =
  (newValue: IdValue<string>) =>
  (dispatch: any, getSTate: () => ApplicationState) => {
    dispatch(actions.setDeclarationFormValue(newValue));
    dispatch(checkIfCurrentStepIsValid());
  };

export const changeAgree =
  (value: boolean) => (dispatch: any, getState: () => ApplicationState) => {
    dispatch(actions.setAgree(value));
    dispatch(checkIfCurrentStepIsValid());
  };

export const onStandardChange = (standardId: string) => (dispatch: any) => {
  dispatch(actions.setIsLoadingStep2(true));
  fetchStandard(standardId)
    .then((standard) => {
      dispatch(actions.setStandard(standard));
    })
    .finally(() => dispatch(actions.setIsLoadingStep2(false)));
};

export const getSession =
  (sessionId: string) => (dispatch: any, getState: () => ApplicationState) => {
    fetchTestSession(sessionId)
      .then((session) => {
        dispatch(actions.setSession(session));
      })
      .then(() => {
        let status = getState().sessionSetup.session?.status?.short;
        if (status === "assist" || status === "approved")
          dispatch(setCurrentStep(3));
      })
      .then(() => dispatch(checkIfCurrentStepIsValid()));
  };

/*-------------- STEP 2 -----------*/
export const getStep2Materials = (
  testCaseIds: Array<string>,
  dispatch: any
) => {
  return new Promise<Blob>((resolve) => {
    dispatch(actions.setIsGeneratingMaterialsStep2(true));
    DowloadTestMaterials(testCaseIds)
      .then((result: Blob) => {
        resolve(result);
      })
      .finally(() => dispatch(actions.setIsGeneratingMaterialsStep2(false)));
  });
};

export const loadStandardIfRequired =
  () => (dispatch: any, getState: () => ApplicationState) => {
    const state = getState();
    if (state.sessionSetup.session?.standardId) {
      if (!!state.sessionSetup.step2.standard === false) {
        dispatch(actions.setIsLoadingStep2(true));
        fetchStandard(state.sessionSetup.session?.standardId)
          .then((standard) => {
            dispatch(actions.setStandard(standard));
            dispatch(getStep2Selections());
          })
          .finally(() => dispatch(actions.setIsLoadingStep2(false)));
      }
    }
  };

const getStep2Selections =
  () => (dispatch: any, getState: () => ApplicationState) => {
    fetchSessionStep2Selections(getState().sessionSetup.session?.id ?? "").then(
      (response: Step2VM) => {
        if (response) {
          let profiles = getState().sessionSetup.step2.profiles?.map(
            (profile) => {
              let selectedProfile = response.selectedProfileOptions?.find(
                (sp) => sp.profileId === profile.id
              );
              if (selectedProfile) {
                return {
                  ...profile,
                  importSelections: {
                    individualOption: {
                      selection: selectedProfile.individualImportOption,
                    },
                    inclusiveOption: {
                      selection: selectedProfile.inclusiveImportOption,
                    },
                    differenceOption: {
                      selection: selectedProfile.differenceImportOption,
                    },
                  },
                  exportSelections: {
                    individualOption: {
                      selection: selectedProfile.individualExportOption,
                    },
                    inclusiveOption: {
                      selection: selectedProfile.inclusiveExportOption,
                    },
                    differenceOption: {
                      selection: selectedProfile.differenceExportOption,
                    },
                  },
                };
              } else {
                return profile;
              }
            }
          );
          dispatch(actions.setProfiles(profiles || []));

          let functions = getState().sessionSetup.step2.functions?.map((fn) => {
            let selectedFunction = response.selectedFunctionOptions?.find(
              (sp) => sp.functionId === fn.id
            );
            if (selectedFunction) {
              return { ...fn, conformityOption: selectedFunction.option };
            } else {
              return fn;
            }
          });
          dispatch(actions.setFunctions(functions || []));

          let testCases = getState().sessionSetup.step2.testCases?.map(
            (testCase) => {
              if (
                response.selectedTestCases?.some(
                  (setelctedTc) => setelctedTc.id === testCase.id
                )
              ) {
                return { ...testCase, selected: true };
              } else {
                return testCase;
              }
            }
          );
          dispatch(actions.setTestCases(testCases ? testCases : []));
          dispatch(actions.setisRefreshingStep2(true));
          findUnmetDependencies(dispatch, getState())
            .then(() => {
              dispatch(checkIfCurrentStepIsValid());
            })
            .finally(() => {
              dispatch(actions.setisRefreshingStep2(false));
            });
        }
      }
    );
  };

export const changeProfileImportExportLevelSelection =
  (id: string, newValue: ImportExportOption, field: ProfileField) =>
  (dispatch: any, getState: () => ApplicationState) => {
    var index =
      getState().sessionSetup.step2.profiles?.findIndex((p) => p.id === id) ??
      0;
    switch (field) {
      case ProfileField.IndividualImportOption:
        dispatch(
          actions.changeProfileIndividualImportSelection({
            index: index,
            value: newValue,
          })
        );
        break;
      case ProfileField.IndividualExportOption:
        dispatch(
          actions.changeProfileIndividualExportSelection({
            index: index,
            value: newValue,
          })
        );
        break;
      case ProfileField.InclusiveImportOption:
        dispatch(
          actions.changeProfileInclusiveImportSelection({
            index: index,
            value: newValue,
          })
        );
        break;
      case ProfileField.InclusiveExportOption:

        if (newValue === 1 && index === 3) {
          isTestCaseTuc50Active = true;
        } else {
          isTestCaseTuc50Active = false;
        }

        dispatch(
          actions.changeProfileInclusiveExportSelection({
            index: index,
            value: newValue,
          })
        );
        break;
      case ProfileField.DifferenceImportOption:
        dispatch(
          actions.changeProfileDifferenceImportSelection({
            index: index,
            value: newValue,
          })
        );
        break;
      case ProfileField.DifferenceExportOption:
        dispatch(
          actions.changeProfileDifferenceExportSelection({
            index: index,
            value: newValue,
          })
        );
        break;
      default:
        break;
    }

    dispatch(actions.setisRefreshingStep2(true));
    findUnmetDependencies(dispatch, getState())
      .then(() => {
        dispatch(updateRequiredTestCases());
      })
      .finally(() => {
        dispatch(checkIfCurrentStepIsValid());
        dispatch(actions.setisRefreshingStep2(false));
      });
  };

export const changeFunctionConformitySelection =
  (id: string, newValue: ConformityLevel) =>
  (dispatch: any, getState: () => ApplicationState) => {
    var index =
      getState().sessionSetup.step2.functions?.findIndex((p) => p.id === id) ??
      0;

    dispatch(
      actions.setFunctionConformityLevel({
        index: index,
        value: newValue,
      })
    );

    dispatch(actions.setisRefreshingStep2(true));
    findUnmetDependencies(dispatch, getState())
      .then(() => {
        dispatch(updateRequiredTestCases());
      })
      .finally(() => {
        dispatch(checkIfCurrentStepIsValid());
        dispatch(actions.setisRefreshingStep2(false));
      });
  };

const findUnmetDependencies = (dispatch: any, state: ApplicationState) =>
  new Promise((resolve, reject) => {
    let profiles = state.sessionSetup.step2.profiles ?? [];
    let functions = state.sessionSetup.step2.functions ?? [];
    let deepCopyProfiles = JSON.parse(JSON.stringify(profiles));
    let deepCopyFunctions = JSON.parse(JSON.stringify(functions));

    findUnmetProfileDependencies(deepCopyProfiles).then((newProfiles) =>
      findUnmetGeneralProfileFunctionDependencies(
        newProfiles,
        deepCopyFunctions
      ).then((newFunctions) =>
        findUnmetFunctionFunctionDependencies(newFunctions).then(
          (newFunctions2) =>
            findUnmetProfileFunctionDependencies(
              newProfiles,
              newFunctions2
            ).then((newFunctions3) => {
              dispatch(
                actions.updateSelections({
                  profiles: newProfiles,
                  functions: newFunctions3,
                })
              );
              resolve(true);
            })
        )
      )
    );
  });

const findUnmetProfileDependencies = (profiles: TestCaseProfileDto[]) =>
  new Promise<TestCaseProfileDto[]>((resolve, reject) => {
    profiles?.forEach((profile, index, array) => {
      let unmetDependencies: Array<string> = [];
      array[index].importSelections!.individualOption!.unmetDependencies = [];
      array[index].importSelections!.differenceOption!.unmetDependencies = [];
      array[index].exportSelections!.individualOption!.unmetDependencies = [];
      array[index].exportSelections!.differenceOption!.unmetDependencies = [];

      if (
        getProfileFieldValue(ProfileField.DifferenceImportOption, profile) ===
          ImportExportOption.Yes &&
        !(
          getProfileFieldValue(ProfileField.InclusiveImportOption, profile) ===
            ImportExportOption.Yes ||
          getProfileFieldValue(ProfileField.IndividualImportOption, profile) ===
            ImportExportOption.Yes
        )
      )
        array[
          index
        ].importSelections?.differenceOption?.unmetDependencies?.push(
          `${profile.name} Difference requires Individual or Inclusive import`
        );

      if (
        getProfileFieldValue(ProfileField.DifferenceExportOption, profile) ===
          ImportExportOption.Yes &&
        !(
          getProfileFieldValue(ProfileField.InclusiveExportOption, profile) ===
            ImportExportOption.Yes ||
          getProfileFieldValue(ProfileField.IndividualExportOption, profile) ===
            ImportExportOption.Yes
        )
      )
        array[
          index
        ].exportSelections?.differenceOption?.unmetDependencies?.push(
          `${profile.name} Difference requires Individual or Inclusive export`
        );

      getUnmetProfileDependencies(ProfileField.IndividualImportOption, profile, profiles).forEach((unmetDependency) => {
        array[
          index
        ].importSelections?.individualOption?.unmetDependencies?.push(
          `${profile.name} requires Individual import: ${
            profiles.find((p) => p.id === unmetDependency)?.name
          }`
        );
      });

      getUnmetProfileDependencies(ProfileField.IndividualExportOption, profile, profiles).forEach((unmetDependency) => {
        array[
          index
        ].exportSelections?.individualOption?.unmetDependencies?.push(
          `${profile.name} requires Individual export: ${
            profiles.find((p) => p.id === unmetDependency)?.name
          }`
        );
      });

      getUnmetProfileDependencies(ProfileField.DifferenceImportOption, profile, profiles).forEach((unmetDependency) => {
        array[
          index
        ].importSelections?.differenceOption?.unmetDependencies?.push(
          `${profile.name} requires Difference import: ${
            profiles.find((p) => p.id === unmetDependency)?.name
          }`
        );
      });

      getUnmetProfileDependencies(ProfileField.DifferenceExportOption, profile, profiles).forEach((unmetDependency) => {
        array[
          index
        ].exportSelections?.differenceOption?.unmetDependencies?.push(
          `${profile.name} requires Difference export: ${
            profiles.find((p) => p.id === unmetDependency)?.name
          }`
        );
      });

      array[index].unmetProfileDependencies = [...unmetDependencies];
    });
    resolve(profiles);
  });

const findUnmetGeneralProfileFunctionDependencies = (
  profiles: TestCaseProfileDto[],
  functions: TestCaseFunctionDto[]
) =>
  new Promise<TestCaseFunctionDto[]>((resolve, reject) => {
    let importLevels: Array<ConformityLevel> = [];
    let exportLevels: Array<ConformityLevel> = [];

    profiles?.forEach((p) => {
      importLevels.push(
        getProfileConformityLevel(
          p.importSelections ?? {},
          p.importExportLevels ?? []
        )
      );
      exportLevels.push(
        getProfileConformityLevel(
          p.exportSelections ?? {},
          p.importExportLevels ?? []
        )
      );
    });

    let minImport = Math.min(
      ...importLevels
        .filter((il) => il !== ConformityLevel.None)
        .map((il) => il as number)
    );
    minImport = minImport !== Infinity ? minImport : 0;
    let minExport = Math.min(
      ...exportLevels
        .filter((el) => el !== ConformityLevel.None)
        .map((el) => el as number)
    );
    minExport = minExport !== Infinity ? minExport : 0;

    let importIndex = functions.findIndex((f) => f.name === "Import");
    let epxportIndex = functions.findIndex((f) => f.name === "Export");

    functions[importIndex].conformityOption = minImport as ConformityLevel;
    functions[epxportIndex].conformityOption = minExport as ConformityLevel;

    resolve(functions);
  });

const findUnmetProfileFunctionDependencies = (
  profiles: TestCaseProfileDto[],
  functions: TestCaseFunctionDto[]
) =>
  new Promise<TestCaseFunctionDto[]>((resolve, reject) => {
    functions?.forEach((f, index, array) => {
      let unmetImportProfileDependencies: Array<FunctionProfileDependencyDto> =
        [];
      let unmetExportProfileDependencies: Array<FunctionProfileDependencyDto> =
        [];
      f.profileDependencies?.forEach((pd) => {
        let profileDependency = profiles?.find((p) => p.id === pd.profileId);
        if (
          getProfileConformityLevel(
            profileDependency?.importSelections ?? {},
            profileDependency?.importExportLevels ?? []
          ) < (f.minImport ?? 0)
        )
          unmetImportProfileDependencies.push(pd);
        if (
          getProfileConformityLevel(
            profileDependency?.exportSelections ?? {},
            profileDependency?.importExportLevels ?? []
          ) < (f.minExport ?? 0)
        )
          unmetExportProfileDependencies.push(pd);
      });

      array[index].unmetImportProfileDependencies = [
        ...unmetImportProfileDependencies,
      ];
      array[index].unmetExportProfileDependencies = [
        ...unmetExportProfileDependencies,
      ];
    });
    resolve(functions);
  });

const findUnmetFunctionFunctionDependencies = (
  functions: TestCaseFunctionDto[]
) =>
  new Promise<TestCaseFunctionDto[]>((resolve, reject) => {
    functions?.forEach((f, index, array) => {
      let unmetFunctionDependencies: Array<FunctionFunctionDependencyDto> = [];
      f.functionDependencies?.forEach((fd) => {
        let functionDependency = functions?.find((f) => f.id === fd.functionId);
        if (
          (functionDependency?.conformityOption ?? 0) <
          (fd.conformityLevel ?? 0)
        ) {
          unmetFunctionDependencies.push(fd);
        }
      });
      array[index].unmetFunctionDependencies = [...unmetFunctionDependencies];
    });
    resolve(functions);
  });

const updateRequiredTestCases =
  () => (dispatch: any, getState: () => ApplicationState) => {
    let state = getState();
    dispatch(actions.setisRefreshingStep2(true));
    getRequiredTestUseCases(
      state.sessionSetup.step2.profiles
        ? state.sessionSetup.step2.profiles?.map((p) => {
            return {
              profileId: p.id,
              individualImportOption:
                p.importSelections?.individualOption?.selection,
              individualExportOption:
                p.exportSelections?.individualOption?.selection,
              inclusiveImportOption:
                p.importSelections?.inclusiveOption?.selection,
              inclusiveExportOption:
                p.exportSelections?.inclusiveOption?.selection,
              differenceImportOption:
                p.importSelections?.differenceOption?.selection,
              differenceExportOption:
                p.exportSelections?.differenceOption?.selection,
            };
          })
        : [],
      state.sessionSetup.step2.functions
        ? state.sessionSetup.step2.functions?.map((fn) => {
            return {
              functionId: fn.id,
              option:
                fn.name === "Import"
                  ? minImportLevelForProfiles(
                      state.sessionSetup.step2.profiles ?? []
                    )
                  : fn.name === "Export"
                  ? minExportLevelForProfiles(
                      state.sessionSetup.step2.profiles ?? []
                    )
                  : fn.conformityOption,
            };
          })
        : []
    )
      .then((result) => {
        let testCases = state.sessionSetup.step2.testCases?.map((testCase) => {
          if (!isTestCaseTuc50Active) {
            result = result.filter(tc => tc?.identifier !== TC50);
          } 
          if (result.some((setelctedTc) => setelctedTc.id === testCase.id)) {
            return { ...testCase, selected: true };
          } else {
            return { ...testCase, selected: false };
          }
        });
        dispatch(actions.setTestCases(testCases ? testCases : []));
      })
      .finally(() => dispatch(actions.setisRefreshingStep2(false)));
  };

/*-------------- STEP 3 -----------*/
export const getStep3Data =
  () => (dispatch: any, getState: () => ApplicationState) => {
    let sessionId = getState().sessionSetup.session?.id;
    let standardId = getState().sessionSetup.session?.standardId;
    dispatch(actions.setIsLoadingStep3(true));

    fetchTestCasesForSession(sessionId ?? "")
      .then((testCases) => {
        dispatch(actions.setStep3TestCases(testCases));
      })
      .then(() => {
        fetchConfigurations(standardId ?? "")
          .then((configurations) => {
            dispatch(actions.setStep3Configurations(configurations));
          })
          .finally(() => {
            dispatch(checkIfCurrentStepIsValid());
            dispatch(actions.setIsLoadingStep3(false));
          });
      });
  };

export const uploadFile = (fileDto: TestFileDto, file: File, dispatch: any) => {
  return new Promise<TestFileDto>((resolve, reject) => {
    dispatch(actions.setIsLoadingStep3Modal(true));
    uploadTestFile(fileDto, file)
      .then((result: TestFileDto) => {
        dispatch(actions.setTestFile(result));
        resolve(result);
      })
      .catch(reject)
      .finally(() => dispatch(actions.setIsLoadingStep3Modal(false)));
  });
};

export const deleteFile = (fileDto: TestFileDto) => (dispatch: any) => {
  dispatch(actions.setIsLoadingStep3Modal(true));
  deleteTestFile(fileDto.id ?? "")
    .then((result) => {
      dispatch(actions.deleteTestFile(fileDto));
    })
    .catch(() => {
      // restClient already showed why the file could not be deleted
    })
    .finally(() => dispatch(actions.setIsLoadingStep3Modal(false)));
};

// export const getTestCasesForSession =
//   () => (dispatch: any, getState: () => ApplicationState) => {
//     let sessionId = getState().sessionSetup.session?.id;
//     dispatch(actions.setIsLoadingStep3(true));

//     fetchTestCasesForSession(sessionId ?? "")
//       .then((testCases) => {
//         dispatch(actions.setStep3TestCases(testCases));
//       })
//       .finally(() => dispatch(actions.setIsLoadingStep3(false)));
//   };

export const setModalTestCase =
  (testCaseId: string) => (dispatch: any, getState: () => ApplicationState) => {
    let testCase = getState().sessionSetup.step3.testCases.find(
      (tc) => tc.id === testCaseId
    );
    let testCaseClone = JSON.parse(JSON.stringify(testCase));
    dispatch(actions.setModalTestCase(testCaseClone));
  };

export const answerInternalEvaluationModal =
  (evaluateInternally: boolean) => (dispatch: any) => {
    dispatch(actions.setTestCasernalEvaluationAnswer(evaluateInternally));
    dispatch(actions.setModalTestCasernalEvaluationAnswer(evaluateInternally));
  };

export const addConfigurationToTestCase =
  () => (dispatch: any, getState: () => ApplicationState) => {
    let configuration = getState().sessionSetup.step3.modalConfiguration;
    if (configuration) {
      dispatch(actions.addConfigurationToTestCase(configuration));
      dispatch(actions.setModalConfiguration({}));
    }
  };

export const removeConfigurationFromTestCase =
  (configurationId: string) =>
  (dispatch: any, getState: () => ApplicationState) => {
    let configuration = getState().sessionSetup.step3.configurations.find(
      (conf) => conf.id === configurationId
    );
    if (configuration) {
      var configurationFiles = getState()
        .sessionSetup.step3.testCases.flatMap((tc) => tc.testSteps)
        .flatMap((ts) => ts?.testFiles)
        .filter((file) => file?.testConfigurationId === configurationId);
      if (configurationFiles.length === 0) {
        dispatch(actions.removeConfigurationFromTestCase(configurationId));
      } else {
        dispatch(actions.setIsLoadingStep3Modal(true));
        deleteTestFiles(configurationFiles)
          .then(() => {
            dispatch(actions.removeConfigurationFromTestCase(configurationId));
            configurationFiles.forEach((confFile) =>
              dispatch(actions.deleteTestFile(confFile ?? {}))
            );
          })
          .catch(() => {
            // restClient already showed why the files could not be deleted
          })
          .finally(() => dispatch(actions.setIsLoadingStep3Modal(false)));
      }
    }
  };

export const changeConfigurationSelect =
  (configurationId: string) =>
  (dispatch: any, getState: () => ApplicationState) => {
    let configuration = getState().sessionSetup.step3.configurations.find(
      (conf) => conf.id === configurationId
    );
    dispatch(actions.setModalConfiguration(configuration ?? {}));
  };

export const clearModalTestCase = () => (dispatch: any) => {
  dispatch(actions.setModalTestCase({}));
  dispatch(actions.setModalConfiguration({}));
  dispatch(checkIfCurrentStepIsValid());
};

export const requireAssistForSession =
  () => (dispatch: any, getState: () => ApplicationState) => {
    dispatch(actions.setIsLoadingStep3(true));
    requireAssist(getState().sessionSetup.session?.id ?? "")
      .then((result) => {
        // a failed request resolves without a result; restClient already showed the reason
        if (result) dispatch(actions.setSessionStatus(result.session?.status ?? {}));
      })
      .finally(() => dispatch(actions.setIsLoadingStep3(false)));
  };
